<template>
  <header 
    class="yeo-header header-scrolled" 
    :class="{ 'sidebar-open': isSidebarOpen }"
    :style="headerStyle"
  >
    <!-- Top Header Bar -->
    <div class="header-top">
      <div class="header-container">
        <!-- Logo Section -->
        <div class="logo-section">
          <a href="#" class="logo-link">
            <img 
              src="/images/aslan-energy-logo-hq.jpg" 
              alt="Aslan Energy" 
              class="logo-img"
            />
          </a>
        </div>

        <!-- Right Controls -->
        <div class="header-controls">
          <!-- Search Bar -->
          <div class="search-container" :class="{ 'search-active': isSearchActive }">
            <input 
              type="text" 
              :placeholder="locale === 'tr' ? 'Arama Yapın...' : 'Search...'"
              class="search-input"
              v-model="searchQuery"
              @focus="handleSearchFocus"
              @blur="handleSearchBlur"
              @input="handleSearchInput"
              @keyup.enter="performSearch"
            />
            <button class="search-btn" @click="performSearch">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M21.71 20.29L18 16.61A9 9 0 1 0 16.61 18l3.68 3.68a1 1 0 0 0 1.42-1.42zM11 18a7 7 0 1 1 7-7 7 7 0 0 1-7 7z"/>
              </svg>
            </button>
            
            <!-- Search Suggestions -->
            <div v-if="showSuggestions && searchSuggestions.length > 0" class="search-suggestions">
              <div 
                v-for="suggestion in searchSuggestions" 
                :key="suggestion.keyword"
                class="suggestion-item"
                @click="selectSuggestion(suggestion)"
              >
                <span class="suggestion-icon">{{ suggestion.icon }}</span>
                <span class="suggestion-text">{{ suggestion.label }}</span>
              </div>
            </div>
          </div>

          <!-- Language Switcher -->
          <div class="language-switcher">
            <button 
              @click="changeLanguage('tr')" 
              :class="{ active: locale === 'tr' }"
              class="lang-btn"
            >
              <img src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 16'%3E%3Crect width='24' height='16' fill='%23e30a17'/%3E%3Ccircle cx='9' cy='8' r='4' fill='white'/%3E%3Ccircle cx='10' cy='8' r='3.2' fill='%23e30a17'/%3E%3Cpolygon points='11.5,5 12.5,7 14.5,7 13,8.5 13.5,10.5 11.5,9.5 9.5,10.5 10,8.5 8.5,7 10.5,7' fill='white'/%3E%3C/svg%3E" 
                   alt="TR" class="flag-icon" />
              TR
            </button>
            <button 
              @click="changeLanguage('en')" 
              :class="{ active: locale === 'en' }"
              class="lang-btn"
            >
              <img src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 16'%3E%3Crect width='24' height='16' fill='%23012169'/%3E%3Cpath d='M0,0 L24,16 M24,0 L0,16' stroke='white' stroke-width='2'/%3E%3Cpath d='M12,0 L12,16 M0,8 L24,8' stroke='white' stroke-width='3'/%3E%3Cpath d='M12,0 L12,16 M0,8 L24,8' stroke='%23C8102E' stroke-width='1.5'/%3E%3C/svg%3E" 
                   alt="EN" class="flag-icon" />
              EN
            </button>
          </div>

          <!-- Menu Toggle Button -->
          <button class="menu-toggle" @click="toggleSidebar" :class="{ 'active': isSidebarOpen }">
            <span class="menu-line"></span>
            <span class="menu-line"></span>
            <span class="menu-line"></span>
          </button>
        </div>
      </div>
    </div>

    <!-- Mobile Overlay -->
    <div 
      class="sidebar-overlay" 
      :class="{ 'active': isSidebarOpen }" 
      @click="closeSidebar"
    ></div>

    <!-- Sidebar Navigation -->
    <nav class="sidebar-nav" :class="{ 'sidebar-active': isSidebarOpen }">
      <div class="sidebar-header">
        <div class="sidebar-logo">
          <img 
            src="/images/aslan-energy-logo-hq.jpg" 
            alt="Aslan Energy" 
            class="sidebar-logo-img"
          />
        </div>
        <button class="sidebar-close" @click="closeSidebar">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
          </svg>
        </button>
      </div>

      <div class="sidebar-content">
        <!-- Search in Sidebar -->
        <div class="sidebar-search">
          <input 
            type="text" 
            :placeholder="locale === 'tr' ? 'Arama Yapın...' : 'Search...'"
            class="sidebar-search-input"
            v-model="searchQuery"
            @keyup.enter="performSearch"
          />
        </div>

        <!-- Main Navigation -->
        <ul class="sidebar-menu">
          <li class="menu-item">
            <a @click="navigateAndClose('.hero-slider')" class="menu-link">
              <span class="menu-icon">🏠</span>
              {{ locale === 'tr' ? 'ANA SAYFA' : 'HOME' }}
            </a>
          </li>
          
          <li class="menu-item">
            <a @click="navigateAndClose('.info-section')" class="menu-link">
              <span class="menu-icon">ℹ️</span>
              {{ locale === 'tr' ? 'INFO SECTION' : 'INFO SECTION' }}
            </a>
          </li>

          <li class="menu-item">
            <a @click="navigateAndClose('.projects-section')" class="menu-link">
              <span class="menu-icon">�️</span>
              {{ locale === 'tr' ? 'PROJELER' : 'PROJECTS' }}
            </a>
          </li>

          <li class="menu-item">
            <a @click="navigateAndClose('.services-section')" class="menu-link">
              <span class="menu-icon">⚙️</span>
              {{ locale === 'tr' ? 'HİZMETLERİMİZ' : 'OUR SERVICES' }}
            </a>
          </li>

          <li class="menu-item">
            <a @click="navigateAndClose('.machinery-section')" class="menu-link">
              <span class="menu-icon">🏭</span>
              {{ locale === 'tr' ? 'MAKİNE PARKIMIZ' : 'OUR MACHINERY' }}
            </a>
          </li>

          <li class="menu-item">
            <a @click="navigateAndClose('.about-section')" class="menu-link">
              <span class="menu-icon">🌱</span>
              {{ locale === 'tr' ? 'SÜRDÜRÜLEBİLİRLİK' : 'SUSTAINABILITY' }}
            </a>
          </li>

          <li class="menu-item">
            <a @click="navigateAndClose('.news-section')" class="menu-link">
              <span class="menu-icon">📰</span>
              {{ locale === 'tr' ? 'MEDYA MERKEZİ' : 'MEDIA CENTER' }}
            </a>
          </li>

          <li class="menu-item">
            <a @click="navigateAndClose('.contact-form-section')" class="menu-link">
              <span class="menu-icon">📞</span>
              {{ locale === 'tr' ? 'İLETİŞİM' : 'CONTACT' }}
            </a>
          </li>

          <li class="menu-item">
            <a @click="navigateAndClose('.about-section')" class="menu-link">
              <span class="menu-icon">🏢</span>
              {{ locale === 'tr' ? 'HAKKIMIZDA' : 'ABOUT US' }}
            </a>
          </li>

          <li class="menu-item">
            <a @click="navigateAndClose('.statistics-section')" class="menu-link">
              <span class="menu-icon">📊</span>
              {{ locale === 'tr' ? 'İSTATİSTİKLER' : 'STATISTICS' }}
            </a>
          </li>

          <li class="menu-item">
            <a @click="navigateAndClose('.news-section')" class="menu-link">
              <span class="menu-icon">📰</span>
              {{ locale === 'tr' ? 'HABERLER' : 'NEWS' }}
            </a>
          </li>

          <li class="menu-item">
            <a @click="navigateAndClose('.contact-form-section')" class="menu-link">
              <span class="menu-icon">📞</span>
              {{ locale === 'tr' ? 'İLETİŞİM' : 'CONTACT' }}
            </a>
          </li>
        </ul>

        <!-- Social Media Links -->
        <div class="sidebar-social">
          <a href="#" class="social-link linkedin" title="LinkedIn">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
            </svg>
          </a>
          <a href="#" class="social-link instagram" title="Instagram">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
            </svg>
          </a>
          <a href="#" class="social-link twitter" title="Twitter">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
            </svg>
          </a>
          <a href="#" class="social-link youtube" title="YouTube">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
            </svg>
          </a>
        </div>
      </div>
    </nav>
  </header>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue'

// Props
const props = defineProps({
  locale: {
    type: String,
    default: 'tr'
  }
})

// Emits
const emit = defineEmits(['changeLanguage', 'navigate'])

// Reactive data
const isSidebarOpen = ref(false)
const isSearchActive = ref(false)
const searchQuery = ref('')
const searchSuggestions = ref([])
const showSuggestions = ref(false)

// Zorla sabit header style
const headerStyle = computed(() => ({
  position: 'fixed',
  top: '0px',
  left: '0px',
  right: '0px',
  width: '100%',
  zIndex: '999999',
  background: 'rgba(255, 255, 255, 0.98)',
  backdropFilter: 'blur(25px)',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
  borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
  visibility: 'visible',
  opacity: '1',
  display: 'block',
  transform: 'none',
  transition: 'none'
}))

// Methods
const changeLanguage = (lang) => {
  emit('changeLanguage', lang)
}

const toggleSidebar = () => {
  isSidebarOpen.value = !isSidebarOpen.value
  if (isSidebarOpen.value) {
    document.body.style.overflow = 'hidden'
  } else {
    document.body.style.overflow = ''
  }
}

const closeSidebar = () => {
  isSidebarOpen.value = false
  document.body.style.overflow = ''
}



const navigateAndClose = (target) => {
  emit('navigate', target)
  closeSidebar()
}

// Search suggestions data
const getAllSuggestions = () => [
  { keyword: 'ana sayfa', label: props.locale === 'tr' ? 'Ana Sayfa' : 'Home', section: '.hero-slider', icon: '🏠' },
  { keyword: 'projeler', label: props.locale === 'tr' ? 'Projeler' : 'Projects', section: '.projects-section', icon: '🔷' },
  { keyword: 'hizmetler', label: props.locale === 'tr' ? 'Hizmetler' : 'Services', section: '.services-section', icon: '⚙️' },
  { keyword: 'makine', label: props.locale === 'tr' ? 'Makine Parkı' : 'Machinery', section: '.machinery-section', icon: '🏗️' },
  { keyword: 'hakkımızda', label: props.locale === 'tr' ? 'Hakkımızda' : 'About Us', section: '.about-section', icon: '🏢' },
  { keyword: 'iletişim', label: props.locale === 'tr' ? 'İletişim' : 'Contact', section: '.contact-form-section', icon: '📞' },
  { keyword: 'haberler', label: props.locale === 'tr' ? 'Haberler' : 'News', section: '.news-section', icon: '📰' },
  { keyword: 'istatistik', label: props.locale === 'tr' ? 'İstatistikler' : 'Statistics', section: '.statistics-section', icon: '📊' },
  { keyword: 'referans', label: props.locale === 'tr' ? 'Referanslar' : 'References', section: '.references-section', icon: '⭐' },
  { keyword: 'bilgi', label: props.locale === 'tr' ? 'Bilgi Kartları' : 'Info Cards', section: '.info-section', icon: 'ℹ️' }
]

// Search input handlers
const handleSearchFocus = () => {
  isSearchActive.value = true
  if (searchQuery.value.trim()) {
    updateSearchSuggestions()
  }
}

const handleSearchBlur = () => {
  setTimeout(() => {
    isSearchActive.value = false
    showSuggestions.value = false
  }, 200)
}

const handleSearchInput = () => {
  updateSearchSuggestions()
}

const updateSearchSuggestions = () => {
  const query = searchQuery.value.toLowerCase().trim()
  if (!query) {
    searchSuggestions.value = []
    showSuggestions.value = false
    return
  }

  const allSuggestions = getAllSuggestions()
  const filtered = allSuggestions.filter(suggestion => 
    suggestion.keyword.toLowerCase().includes(query) ||
    suggestion.label.toLowerCase().includes(query) ||
    query.includes(suggestion.keyword.toLowerCase())
  ).slice(0, 5) // Maksimum 5 öneri

  searchSuggestions.value = filtered
  showSuggestions.value = filtered.length > 0
}

const selectSuggestion = (suggestion) => {
  searchQuery.value = suggestion.label
  emit('navigate', suggestion.section)
  searchQuery.value = ''
  isSearchActive.value = false
  showSuggestions.value = false
  closeSidebar()
}

// Search functionality
const performSearch = () => {
  const query = searchQuery.value.toLowerCase().trim()
  if (!query) return

  // Önce suggestions'da ara
  const allSuggestions = getAllSuggestions()
  const exactMatch = allSuggestions.find(suggestion => 
    suggestion.keyword.toLowerCase() === query ||
    suggestion.label.toLowerCase() === query
  )

  if (exactMatch) {
    emit('navigate', exactMatch.section)
    searchQuery.value = ''
    isSearchActive.value = false
    showSuggestions.value = false
    closeSidebar()
    return
  }

  // Kısmi eşleşme ara
  const partialMatch = allSuggestions.find(suggestion => 
    suggestion.keyword.toLowerCase().includes(query) ||
    suggestion.label.toLowerCase().includes(query) ||
    query.includes(suggestion.keyword.toLowerCase())
  )

  if (partialMatch) {
    emit('navigate', partialMatch.section)
    searchQuery.value = ''
    isSearchActive.value = false
    showSuggestions.value = false
    closeSidebar()
    return
  }

  // Geniş anahtar kelime araması
  const extendedMappings = {
    // Ek anahtar kelimeler
    'hero': '.hero-slider',
    'slider': '.hero-slider',
    'güneş': '.info-section',
    'solar': '.info-section',
    'enerji': '.info-section',
    'energy': '.info-section',
    'müşteri': '.references-section',
    'customer': '.references-section',
    'ekipman': '.machinery-section',
    'equipment': '.machinery-section',
    'sayı': '.statistics-section',
    'number': '.statistics-section',
    'proje': '.projects-section',
    'project': '.projects-section',
    'hizmet': '.services-section',
    'service': '.services-section',
    'çözüm': '.services-section',
    'solution': '.services-section',
    'şirket': '.about-section',
    'company': '.about-section',
    'firma': '.about-section',
    'haber': '.news-section',
    'news': '.news-section',
    'duyuru': '.news-section',
    'announcement': '.news-section',
    'blog': '.news-section',
    'telefon': '.contact-form-section',
    'phone': '.contact-form-section',
    'email': '.contact-form-section',
    'mail': '.contact-form-section',
    'adres': '.contact-form-section',
    'address': '.contact-form-section'
  }

  // Geniş arama
  let targetSection = null
  for (const [keyword, section] of Object.entries(extendedMappings)) {
    if (query.includes(keyword) || keyword.includes(query)) {
      targetSection = section
      break
    }
  }

  if (targetSection) {
    emit('navigate', targetSection)
    searchQuery.value = ''
    isSearchActive.value = false
    showSuggestions.value = false
    closeSidebar()
  } else {
    // Eşleşme bulunamadı, öneri göster
    const availableKeywords = props.locale === 'tr' 
      ? 'ana sayfa, projeler, hizmetler, makine, hakkımızda, iletişim, haberler, istatistik'
      : 'home, projects, services, machinery, about, contact, news, statistics'
    
    alert(props.locale === 'tr' 
      ? `"${query}" için sonuç bulunamadı.\n\nDeneyebileceğiniz kelimeler: ${availableKeywords}`
      : `No results found for "${query}".\n\nTry these keywords: ${availableKeywords}`
    )
  }
}

// Scroll functions removed - now handled in App.vue

// Lifecycle
onMounted(() => {
  // Header'a zorla sabit style uygula
  const headerEl = document.querySelector('.yeo-header')
  if (headerEl) {
    headerEl.style.cssText = `
      position: fixed !important;
      top: 0 !important;
      left: 0 !important;
      right: 0 !important;
      width: 100% !important;
      z-index: 999999 !important;
      background: rgba(255, 255, 255, 0.98) !important;
      backdrop-filter: blur(25px) !important;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15) !important;
      border-bottom: 1px solid rgba(0, 0, 0, 0.1) !important;
      visibility: visible !important;
      opacity: 1 !important;
      display: block !important;
      transform: none !important;
      transition: none !important;
    `
  }
  
  // Body'ye padding ekle
  document.body.style.paddingTop = '96px'
  
  // Her 100ms'de kontrol et ve düzelt
  const intervalId = setInterval(() => {
    const header = document.querySelector('.yeo-header')
    if (header && header.style.position !== 'fixed') {
      header.style.cssText = `
        position: fixed !important;
        top: 0 !important;
        left: 0 !important;
        right: 0 !important;
        width: 100% !important;
        z-index: 999999 !important;
        background: rgba(255, 255, 255, 0.98) !important;
        backdrop-filter: blur(25px) !important;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15) !important;
        visibility: visible !important;
        opacity: 1 !important;
        display: block !important;
      `
    }
  }, 100)
  
  // Component unmount olduğunda interval'i temizle
  onUnmounted(() => {
    clearInterval(intervalId)
  })
})

onUnmounted(() => {
  document.body.style.overflow = ''
})
</script>

<style scoped>
/* Import CSS Variables */
:root {
  --primary-gold: #F7C52D;
  --primary-gold-light: #F9D55A;
  --primary-gold-dark: #E6B800;
}

/* Header Container */
.yeo-header {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  width: 100% !important;
  z-index: 99999 !important;
  background: rgba(255, 255, 255, 0.98) !important;
  backdrop-filter: blur(25px) !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1) !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15) !important;
  transition: none !important;
  transform: none !important;
  will-change: auto !important;
  pointer-events: auto !important;
  visibility: visible !important;
  opacity: 1 !important;
  display: block !important;
}

.yeo-header.header-scrolled {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  width: 100% !important;
  z-index: 99999 !important;
  background: rgba(255, 255, 255, 0.98) !important;
  box-shadow: 0 4px 25px rgba(0, 0, 0, 0.15) !important;
  backdrop-filter: blur(25px) !important;
  transition: none !important;
  transform: none !important;
  visibility: visible !important;
  opacity: 1 !important;
  display: block !important;
}

.header-top {
  padding: 0 !important;
  position: relative !important;
  width: 100% !important;
  height: 96px !important;
}

.header-container {
  max-width: 1600px !important;
  margin: 0 auto !important;
  padding: 0 3rem !important;
  display: flex !important;
  justify-content: space-between !important;
  align-items: center !important;
  height: 96px !important;
  position: relative !important;
  width: 100% !important;
  box-sizing: border-box !important;
}

/* Logo Section */
.logo-section {
  display: flex;
  align-items: center;
}

.logo-link {
  text-decoration: none;
  display: flex;
  align-items: center;
}

.logo-img {
  height: 65px;
  width: auto;
  transition: transform 0.3s ease;
}

.logo-link:hover .logo-img {
  transform: scale(1.05);
}

/* Header Controls */
.header-controls {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

/* Search Container */
.search-container {
  position: relative;
  display: flex;
  align-items: center;
  background: #f8f9fa;
  border-radius: 25px;
  padding: 0.5rem 1rem;
  transition: all 0.3s ease;
  max-width: 200px;
  z-index: 1001;
}

.search-container.search-active {
  background: white;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  max-width: 300px;
}

.search-input {
  border: none;
  background: transparent;
  outline: none;
  padding: 0.25rem 0.5rem;
  font-size: 0.9rem;
  color: #333;
  width: 100%;
}

.search-input::placeholder {
  color: #999;
}

.search-btn {
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  padding: 0.25rem;
  transition: color 0.3s ease;
}

.search-btn:hover {
  color: #388e3c;
}

/* Search Suggestions */
.search-suggestions {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  max-height: 300px;
  overflow-y: auto;
  margin-top: 4px;
}

.suggestion-item {
  display: flex;
  align-items: center;
  padding: 12px 15px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  border-bottom: 1px solid #f0f0f0;
}

.suggestion-item:hover {
  background-color: #f8f9fa;
}

.suggestion-item:last-child {
  border-bottom: none;
}

.suggestion-icon {
  margin-right: 10px;
  font-size: 16px;
  width: 20px;
  text-align: center;
}

.suggestion-text {
  font-size: 14px;
  color: #333;
  font-weight: 500;
}

/* Language Switcher */
.language-switcher {
  display: flex;
  gap: 0.5rem;
}

.lang-btn {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.4rem 0.8rem;
  border: 1px solid #ddd;
  background: white;
  color: #666;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.85rem;
  font-weight: 500;
}

.flag-icon {
  width: 16px;
  height: 12px;
  border-radius: 2px;
}

.lang-btn:hover {
  border-color: #388e3c;
  color: #388e3c;
}

.lang-btn.active {
  background: #388e3c;
  color: white;
  border-color: #388e3c;
}

/* Menu Toggle */
.menu-toggle {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 40px;
  height: 40px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  gap: 4px;
}

.menu-line {
  width: 24px;
  height: 2px;
  background: #333;
  transition: all 0.3s ease;
  border-radius: 1px;
}

.menu-toggle.active .menu-line:nth-child(1) {
  transform: rotate(45deg) translate(6px, 6px);
}

.menu-toggle.active .menu-line:nth-child(2) {
  opacity: 0;
}

.menu-toggle.active .menu-line:nth-child(3) {
  transform: rotate(-45deg) translate(6px, -6px);
}

/* Sidebar Overlay */
.sidebar-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s ease;
}

.sidebar-overlay.active {
  opacity: 1;
  visibility: visible;
}

/* Sidebar Navigation */
.sidebar-nav {
  position: fixed;
  top: 0;
  right: -400px;
  width: 400px;
  height: 100vh;
  background: white;
  z-index: 1001;
  transition: right 0.3s ease;
  overflow-y: auto;
  box-shadow: -5px 0 20px rgba(0, 0, 0, 0.1);
}

.sidebar-nav.sidebar-active {
  right: 0;
}

.sidebar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #eee;
}

.sidebar-logo-img {
  height: 35px;
  width: auto;
}

.sidebar-close {
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  transition: all 0.3s ease;
}

.sidebar-close:hover {
  background: #f5f5f5;
  color: #333;
}

.sidebar-content {
  padding: 1rem 0;
}

/* Sidebar Search */
.sidebar-search {
  padding: 1rem 2rem;
  border-bottom: 1px solid #eee;
}

.sidebar-search-input {
  width: 100%;
  padding: 0.8rem 1rem;
  border: 1px solid #ddd;
  border-radius: 25px;
  outline: none;
  font-size: 0.9rem;
  transition: border-color 0.3s ease;
}

.sidebar-search-input:focus {
  border-color: #388e3c;
}

/* Sidebar Menu */
.sidebar-menu {
  list-style: none;
  padding: 0;
  margin: 0;
}

.menu-item {
  border-bottom: 1px solid #f5f5f5;
}

.menu-link {
  display: flex;
  align-items: center;
  padding: 1rem 2rem;
  color: #333;
  text-decoration: none;
  font-weight: 500;
  font-size: 0.9rem;
  letter-spacing: 0.5px;
  transition: all 0.3s ease;
  cursor: pointer;
}

.menu-link:hover {
  background: #f8f9fa;
  color: #388e3c;
  padding-left: 2.5rem;
}

.menu-icon {
  margin-right: 1rem;
  font-size: 1.1rem;
  width: 20px;
  display: flex;
  justify-content: center;
}

.dropdown-toggle {
  justify-content: space-between;
}

.dropdown-arrow {
  transition: transform 0.3s ease;
}

.dropdown-active .dropdown-arrow {
  transform: rotate(180deg);
}

.dropdown-menu {
  list-style: none;
  padding: 0;
  margin: 0;
  background: #f8f9fa;
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.3s ease;
}

.dropdown-active .dropdown-menu {
  max-height: 200px;
}

.dropdown-active .sections-menu {
  max-height: 400px;
  overflow-y: auto;
}

.dropdown-link {
  display: block;
  padding: 0.8rem 4rem;
  color: #666;
  text-decoration: none;
  font-size: 0.85rem;
  transition: all 0.3s ease;
  position: relative;
}

.dropdown-link:hover {
  background: #e9ecef;
  color: #388e3c;
  padding-left: 4.5rem;
}

/* Section dropdown specific styles */
.sections-menu .dropdown-link {
  padding: 0.7rem 3.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.sections-menu .dropdown-link:hover {
  padding-left: 4rem;
  background: linear-gradient(90deg, #f8f9fa 0%, #e3f2fd 100%);
  border-left: 3px solid var(--primary-gold);
}

.section-number {
  font-weight: 600;
  color: var(--primary-gold);
  min-width: 25px;
  font-size: 0.8rem;
}

.sections-menu .dropdown-link:hover .section-number {
  color: var(--primary-gold-dark);
}

/* Sidebar Social */
.sidebar-social {
  padding: 2rem;
  border-top: 1px solid #eee;
  display: flex;
  gap: 1rem;
  justify-content: center;
}

.social-link {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #f8f9fa;
  color: #666;
  text-decoration: none;
  transition: all 0.3s ease;
}

.social-link:hover {
  transform: translateY(-2px);
}

.social-link.linkedin:hover {
  background: #0077b5;
  color: white;
}

.social-link.instagram:hover {
  background: linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%);
  color: white;
}

.social-link.twitter:hover {
  background: #1da1f2;
  color: white;
}

.social-link.youtube:hover {
  background: #ff0000;
  color: white;
}

/* Scroll to Top Button */
/* Scroll to top button moved to App.vue */

/* Responsive Design */
@media (max-width: 768px) {
  .header-container {
    padding: 0 1.5rem !important;
    height: 84px !important;
  }
  
  .logo-img {
    height: 55px;
  }
  
  .header-top {
    height: 84px !important;
  }
  
  .search-container {
    display: none;
  }
  
  .sidebar-nav {
    width: 100%;
    right: -100%;
  }
  
  .menu-link {
    padding: 1.2rem 1.5rem;
  }
  
  .dropdown-link {
    padding: 1rem 3rem;
  }
  
  /* Scroll to top styles moved to App.vue */
}

@media (max-width: 480px) {
  .header-container {
    height: 78px !important;
    padding: 0 1rem !important;
  }
  
  .logo-img {
    height: 50px;
  }
  
  .header-top {
    height: 78px !important;
  }
  
  .lang-btn {
    padding: 0.3rem 0.6rem;
    font-size: 0.8rem;
  }
  
  .flag-icon {
    width: 14px;
    height: 10px;
  }
  
  .scroll-to-top {
    bottom: 15px;
    right: 15px;
    width: 40px;
    height: 40px;
  }
}
</style>